import { HimmaContext } from '../context/HimmaContext';
import { useState, useCallback, useContext } from 'react';
import {
  EditUserRequestSchema,
  ToggleUserStatusRequest,
  DeleteUsersRequest,
  AddUserSchema,
} from '../client-sdk';

export const useCustomerApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create Customer
  const createUser = useCallback(
    async (userData: AddUserSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.createUser(userData);
        if (response?.data) {
          return response.data;
        }
        throw new Error('Failed to create user: No response data');
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'An error occurred while creating user';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Edit Customer
  const editCustomerById = useCallback(
    async (userId: any, customerData: EditUserRequestSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editUserById(userId, customerData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // List Customer
  const getAllCustomer = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Only include search parameter if it has a value
        const searchParam =
          query.search && query.search.trim() !== '' ? query.search : undefined;
        const response = await api.getAllUsers(
          query.page,
          query.limit,
          query.sortBy,
          query.sortOrder,
          searchParam
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Delete Customer
  const deleteCustomerById = useCallback(
    async (query: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const deleteCustomer: DeleteUsersRequest = {
          usersIds: query,
        };
        const response = await api.deleteUsers(deleteCustomer);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // Block/Unblock Customer
  const blockOrUnblockCustomerById = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const toggleSubAdminStatusRequest: ToggleUserStatusRequest = {
          userIds: Array.isArray(query.id) ? query.id : [query.id],
          status: query.status,
        };
        const response = await api.toggleUserStatus(
          toggleSubAdminStatusRequest
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    createUser,
    loading,
    error,
    getAllCustomer,
    deleteCustomerById,
    blockOrUnblockCustomerById,
    editCustomerById,
  };
};
